import { CaptureMetadata } from "@/types/annotations";
import { renderAnnotatedImage, RenderOptions } from "./annotationRenderingService";
import { getAnnotationMetadata, getStorageDownloadURL } from "@/utils/entryUtils";
import type { Timestamp } from "firebase/firestore";

// ============================================================================
// TYPES
// ============================================================================

export interface ExportOptions {
  showLength: boolean;
  showWidth: boolean;
  showMass: boolean;
  showKeypoints?: boolean;
  showSkeleton?: boolean;
  showMeasureBoundary?: boolean;
  showKeypointLabels?: boolean;
  showText?: boolean; // Toggle for measurement text labels (default true)
  annotationClassNames?: string[];
  annotationClassColorOrder?: string[];
}

export const DEFAULT_ANNOTATED_IMAGE_EXPORT_OPTIONS: ExportOptions = {
  showLength: true,
  showWidth: true,
  showMass: true,
  showKeypoints: true,
  showSkeleton: true,
  showMeasureBoundary: true,
  showText: true,
};

export function buildAnnotatedImageRenderOptions(options: Partial<ExportOptions> = {}): RenderOptions {
  const mergedOptions = {
    ...DEFAULT_ANNOTATED_IMAGE_EXPORT_OPTIONS,
    ...options,
  };

  return {
    showLength: mergedOptions.showLength,
    showWidth: mergedOptions.showWidth,
    showMass: mergedOptions.showMass,
    showKeypoints: mergedOptions.showKeypoints,
    showSkeleton: mergedOptions.showSkeleton,
    showMeasureBoundary: mergedOptions.showMeasureBoundary,
    showKeypointLabels: mergedOptions.showKeypointLabels,
    showText: mergedOptions.showText,
    annotationClassNames: mergedOptions.annotationClassNames,
    annotationClassColorOrder: mergedOptions.annotationClassColorOrder,
    jpegQuality: 0.92,
  };
}

export async function getImageUrlAndMetadata(
  imgCloudPath: string,
  captureMetadataCloudPath: string,
  updatedAt?: Timestamp
): Promise<{ imageUrl: string; metadata: CaptureMetadata }> {
  // Get image URL
  const imageUrl = await getStorageDownloadURL(imgCloudPath);

  // Get metadata
  const metadata = await getAnnotationMetadata(captureMetadataCloudPath, updatedAt);
  if (!metadata) {
    throw new Error("No annotation metadata found for this image");
  }

  return { imageUrl, metadata };
}

/**
 * Export a single annotated image (for entry-level download).
 *
 * @param imgCloudPath - Firebase Storage path to image
 * @param captureMetadataCloudPath - Firebase Storage path to metadata
 * @param options - Which measurements to display
 * @returns JPEG blob of annotated image
 */
export async function exportAnnotatedImageForEntry(
  imgCloudPath: string,
  captureMetadataCloudPath: string,
  options: ExportOptions,
  updatedAt?: Timestamp
): Promise<Blob> {
  // Get image URL
  const imageUrl = await getStorageDownloadURL(imgCloudPath);

  // Get metadata
  const metadata = await getAnnotationMetadata(captureMetadataCloudPath, updatedAt);
  if (!metadata) {
    throw new Error("No annotation metadata found for this image");
  }

  // Render annotated image
  return renderAnnotatedImage(imageUrl, metadata, buildAnnotatedImageRenderOptions(options));
}

/**
 * Get the original image as a blob (for downloading alongside annotated version).
 *
 * @param imgCloudPath - Firebase Storage path to image
 * @returns JPEG blob of original image
 */
export async function getOriginalImageBlob(imgCloudPath: string): Promise<Blob> {
  const imageUrl = await getStorageDownloadURL(imgCloudPath);
  const response = await fetch(imageUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.statusText}`);
  }
  return response.blob();
}

/**
 * Trigger a browser download of a blob.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
